import { ObservableCollection } from './../../observable';

import { Constructor, Tuple } from '@skylab-shared/types';
import { ModelBase } from './model-base';
import { ModelMetadataContainer, ModelMetadataProvider } from './model-metadata-container';
import { PropertyType } from './property';
import { PropertyMetadata } from './property-metadata';

export class Domain {
    public static determineDomain(root: Constructor<ModelBase>): Domain {
        const getType = (p: PropertyMetadata) => p.type === ObservableCollection ? p.config.type : p.type;
        const traverse = (model: Constructor<ModelBase>, propertyMetadata: PropertyMetadata | null): DomainNode | undefined => {
            const metadata = <ModelMetadataContainer>Reflect.getMetadata('skl:model', model);
            if (!metadata) return;

            const children = metadata.properties
                .filter(p => ModelBase.isPrototypeOf(getType(p)))
                .map(p => traverse(getType(p), p))
                .filter((node): node is DomainNode => node !== undefined);
            return new DomainNode(model, children, propertyMetadata);
        };
        return new Domain(traverse(root, null)!);
    }

    public static getAllDescendantProperties<TModel extends ModelBase>(property: PropertyType<TModel>): PropertyType<ModelBase>[] {
        const [modelType, propertyName] = property;
        const { properties } = getModelMetadata(modelType);
        const propertyMetadata = properties.find(p => p.propertyName === propertyName);
        if (propertyMetadata && (ModelBase.isPrototypeOf(propertyMetadata.type) || (propertyMetadata.config?.type && ModelBase.isPrototypeOf(propertyMetadata.config.type)))) {
            const type = ModelBase.isPrototypeOf(propertyMetadata.type) ? propertyMetadata.type : propertyMetadata.config.type;
            const { properties } = getModelMetadata(type);
            return properties
                .map(p => Domain.getAllDescendantProperties([type, <keyof ModelBase>p.propertyName]))
                .reduce<PropertyType<ModelBase>[]>((result, curr) => [...result, ...curr], []);
        }
        return [<PropertyType<ModelBase>>property];
    }

    private constructor(public readonly root: DomainNode) { }

    public traverseDomainNodes(action: (node: DomainNode, parent: DomainNode | null) => void): void {
        const traverse = (node: DomainNode, action: (node: DomainNode, parent: DomainNode | null) => void, parent: DomainNode | null): void => {
            action(node, parent);
            node.children.forEach(child => traverse(child, action, node));
        };
        traverse(this.root, action, null);
    }

    /**
     * @return An array of tuples (model_instance, proprety_name).
     */
    public getModels(model: ModelBase): Tuple<ModelBase, string>[] {
        const domainNode = this.findDomainNodeForModel(model);
        if (!domainNode) {
            console.error(`Domain node for given model (${model.constructor.name}) not found.`);
            return [];
        }

        const models: Tuple<ModelBase, string>[] = [];
        Domain.traverse(domainNode, model, null, (node, value, propertyName) => {
            if (value instanceof ModelBase) {
                models.push([value, propertyName]);
            }
        });
        return models;
    }

    /**
     * @return An array of tuples (collection_instance, property_name).
     */
    public getCollections(model: ModelBase): Tuple<ObservableCollection<ModelBase>, string>[] {
        const domainNode = this.findDomainNodeForModel(model);
        if (!domainNode) {
            console.error(`Domain node for given model (${model.constructor.name}) not found.`);
            return [];
        }

        const collections: Tuple<ObservableCollection<ModelBase>, string>[] = [];
        Domain.traverse(domainNode, model, null, (node, value, propertyName) => {
            if (value instanceof ObservableCollection) {
                collections.push([value, propertyName]);
            }
        });
        return collections;
    }

    private findDomainNodeForModel(model: ModelBase): DomainNode | null {
        let domainNode: DomainNode | null = null;
        this.traverseDomainNodes(node => {
            if (domainNode !== null || !(model instanceof node.modelType)) {
                return;
            }

            domainNode = node;
        });
        return domainNode;
    }

    private static traverse(node: DomainNode, model: ModelBase, propertyName: string | null, action: (node: DomainNode, value: any, propertyName: string) => void): void {
        if (!model || node.modelType !== model.constructor)
            return;
        action(node, model, propertyName || '');
        node.children.forEach(child => {
            const propName = child.property?.propertyName;
            if (!propName) return;
            const value = model.get<unknown>(propName);
            if (value instanceof ModelBase) {
                Domain.traverse(child, value, propName, action);
            } else if (value instanceof ObservableCollection) {
                action(child, value, propName);
                value.forEach(v => Domain.traverse(child, v, propName, action));
            }
        });
    }
}

export class DomainNode {
    public constructor(public readonly modelType: Constructor<ModelBase>,
        public readonly children: DomainNode[] = [],
        public readonly property: PropertyMetadata | null = null) { }
}

const getModelMetadata = (type: Constructor<ModelBase>) => <ModelMetadataProvider>Reflect.getMetadata('skl:model', type);
